import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { BlueprintCard } from "./BlueprintCard";

import { useBlueprints } from "@/api/creativesphere";
import { type BlueprintMessage } from "@/types/creativesphere";

export type BlueprintPickerStepProps = {
  siteId: string;
  onSelect: (blueprint: BlueprintMessage) => void;
  onBack: () => void;
};

export function BlueprintPickerStep({
  siteId,
  onSelect,
  onBack,
}: BlueprintPickerStepProps) {
  const { data, isLoading, isError } = useBlueprints(siteId);
  const blueprints = data?.blueprints ?? [];
  const hasResults = blueprints?.length > 0;

  // Show a spinner while the query is in-flight so slow connections don't flash
  // the "No blueprints found" empty state before the grid arrives.
  if (isLoading) {
    return (
      <Box
        sx={{
          px: 6,
          py: 10,
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!hasResults) {
    return (
      <Box sx={{ px: 6, py: 4, flex: 1 }}>
        <Typography variant="h5" gutterBottom>
          {isError ? "Couldn't load blueprints" : "No blueprints found"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 6, py: 4, flex: 1 }}>
      <Typography variant="h5" gutterBottom>
        Pick a blueprint
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choosing a blueprint opens the canvas with its scene already set.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(3, 1fr)",
            sm: "repeat(4, 1fr)",
          },
          gap: 3,
        }}
      >
        {blueprints.map((blueprint) => (
          <BlueprintCard
            key={blueprint.slug}
            blueprint={blueprint}
            onSelect={onSelect}
          />
        ))}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
    </Box>
  );
}
